import {
  AfterContentInit,
  AfterViewInit,
  Component,
  computed,
  ContentChildren,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  QueryList,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableDataSource } from 'common/table/table-data-source';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import {
  Action,
  ActionScope,
  EditingSupport,
  supportsMultiSelect,
} from 'common/table/editing-support.interface';
import { MatCheckbox } from '@angular/material/checkbox';
import { SelectionModel } from '@angular/cdk/collections';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { HasId } from 'common/table/has-id.interface';
import { NgClass } from '@angular/common';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  FilteringSupport,
  FilterValues,
  TableRowFilter,
} from 'common/table/filtering-support.interface';
import { MatOptionModule } from '@angular/material/core';
import { PagedTableFiltersComponent } from 'common/table/paged-table-filters.component';
import { IconDirective } from 'common/icon/icon.directive';
import { ButtonDirective } from 'common/button/button.directive';

@Component({
  selector: 'comp-paged-table',
  templateUrl: 'paged-table.component.html',
  styleUrls: ['./paged-table.component.scss'],
  hostDirectives: [
    {
      directive: MatSort,
      outputs: ['matSortChange'],
    },
  ],
  imports: [
    MatTable,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatMenu,
    MatMenuItem,
    TranslateModule,
    MatMenuTrigger,
    MatCheckbox,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatNoDataRow,
    MatPaginator,
    NgClass,
    MatSortModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    PagedTableFiltersComponent,
    IconDirective,
    ButtonDirective,
  ],
})
export class PagedTableComponent<T extends HasId>
  implements AfterContentInit, AfterViewInit, OnDestroy
{
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sort = inject(MatSort);
  private readonly registeredProjectedColumns = new Map<string, MatColumnDef>();

  dataSource = input.required<TableDataSource<T>>();
  columns = input.required<string[]>();
  modelObjectProperty = input.required<string>();
  editingSupport = input<EditingSupport<T>>();
  filteringSupport = input<FilteringSupport>();
  enableFilter = input<boolean>(false);
  hidePaginator = input<boolean>(false);

  rowClass = input<(row: T) => string | string[] | Set<string> | Record<string, boolean>>(() => '');

  rowClicked = output<T>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatTable, { static: true }) table!: MatTable<T>;
  @ContentChildren(MatColumnDef) projectedColumnDefs!: QueryList<MatColumnDef>;
  @ViewChild('tableScroll', { read: ElementRef }) tableScroll!: ElementRef<HTMLElement>;
  @ViewChild('tableElement', { read: ElementRef }) tableElement!: ElementRef<HTMLElement>;

  isDeleteMode = signal(false);
  isRunningAction = signal(false);
  hasHorizontalScroll = signal(false);
  private selectedColumnIds = signal<string[] | null>(null);

  protected tableFilters = computed<TableRowFilter[]>(() => {
    const filters = this.filteringSupport()?.getFilters() ?? [];

    return filters.map((filter) => {
      if (filter.type !== 'columns') {
        return filter;
      }

      return {
        ...filter,
        columnOptions: this.columns().map((column) => ({
          id: column,
          labelKey: `${this.modelObjectProperty()}.properties.${column}`,
        })),
      };
    });
  });

  private resizeObserver?: ResizeObserver;

  private readonly visibleColumns = computed(() => {
    const selectedColumnIds = this.selectedColumnIds();

    if (!selectedColumnIds) {
      return this.columns();
    }

    return this.columns().filter((column) => selectedColumnIds.includes(column));
  });

  columnsToDisplay = computed(() => {
    const columns = this.visibleColumns();

    if (!this.editingSupport()) {
      return columns;
    }

    if (this.isDeleteMode()) {
      return [...columns, 'select'];
    }

    return [...columns, 'menu'];
  });

  selection: SelectionModel<T>;

  constructor() {
    const initialSelection: T[] = [];
    const allowMultiSelect = true;
    this.selection = new SelectionModel<T>(allowMultiSelect, initialSelection);
  }

  ngAfterContentInit(): void {
    this.syncProjectedColumns(this.projectedColumnDefs);
    this.projectedColumnDefs.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((columnDefs: QueryList<MatColumnDef>) => {
        this.syncProjectedColumns(columnDefs);
      });
    this.sort.sortChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.sortSettingsChanged(event));
  }

  ngAfterViewInit(): void {
    this.dataSource().paginator = this.paginator;
    this.observeHorizontalScrollState();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.registeredProjectedColumns.forEach((columnDef) => {
      this.table.removeColumnDef(columnDef);
    });
    this.registeredProjectedColumns.clear();
  }

  private syncProjectedColumns(columnDefs: QueryList<MatColumnDef>): void {
    const nextColumns = new Map(columnDefs.map((columnDef) => [columnDef.name, columnDef]));

    this.registeredProjectedColumns.forEach((columnDef, name) => {
      if (!nextColumns.has(name)) {
        this.table.removeColumnDef(columnDef);
        this.registeredProjectedColumns.delete(name);
      }
    });

    nextColumns.forEach((columnDef, name) => {
      const previous = this.registeredProjectedColumns.get(name);
      if (!previous) {
        this.table.addColumnDef(columnDef);
      } else if (previous !== columnDef) {
        this.table.removeColumnDef(previous);
        this.table.addColumnDef(columnDef);
      }
      this.registeredProjectedColumns.set(name, columnDef);
    });
  }

  private observeHorizontalScrollState(): void {
    const scrollElement = this.tableScroll?.nativeElement;
    const tableElement = this.tableElement?.nativeElement;

    if (!(scrollElement instanceof Element) || !(tableElement instanceof Element)) {
      return;
    }

    const updateHorizontalScrollState = () => {
      const hasHorizontalScroll = scrollElement.scrollWidth > scrollElement.clientWidth;

      if (this.hasHorizontalScroll() !== hasHorizontalScroll) {
        this.hasHorizontalScroll.set(hasHorizontalScroll);
      }
    };

    this.resizeObserver = new ResizeObserver(updateHorizontalScrollState);

    this.resizeObserver.observe(scrollElement);
    this.resizeObserver.observe(tableElement);

    requestAnimationFrame(updateHorizontalScrollState);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource().data.length;
    return numRows > 0 && numSelected == numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource().data.forEach((row) => this.selection.select(row));
  }

  protected enterDeleteMode() {
    if (this.editingSupport()) {
      this.isDeleteMode.set(true);
    }
  }

  protected async handleDeleteMultiple() {
    const editingSupport = this.editingSupport();
    if (editingSupport) {
      const deleteAction = this.getMatchingAction('menu', 'delete');
      if (supportsMultiSelect(deleteAction)) {
        await deleteAction.onMany(this.selection.selected);
        this.selection.clear();
        this.isDeleteMode.set(false);
        this.dataSource().refreshFromRoute(this.route);
      }
    }
  }

  protected handleCancelDelete() {
    this.selection.clear();
    this.isDeleteMode.set(false);
  }

  protected getRowClass(row: T): string | string[] | Set<string> | Record<string, boolean> {
    return this.rowClass()(row);
  }

  protected handleRowClicked(row: T, event: MouseEvent): void {
    const target = event.target as HTMLElement | null;

    // Ignore clicks that originate from interactive controls inside the row
    // (buttons, inputs, links, menus, checkboxes, etc.).
    if (target?.closest('button, a, input, label, mat-checkbox, .mat-mdc-menu-panel')) {
      return;
    }

    this.rowClicked.emit(row);
  }

  protected handlePageEvent(event: PageEvent) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: event.pageIndex,
        pageSize: event.pageSize,
      },
      queryParamsHandling: 'merge',
    });
  }

  getActions(scope: ActionScope, row?: T): Action<T>[] {
    const editingSupport = this.editingSupport();
    if (!editingSupport) {
      return [];
    }

    return editingSupport
      .getActions()
      .filter((action) => action.scopes.includes(scope) && !action.disabled(row));
  }

  isSupported(scope: ActionScope, actionId: string) {
    return this.getMatchingAction(scope, actionId) !== null;
  }

  getMatchingAction(scope: ActionScope, actionId: string): Action<T> | null {
    const matchingActions = this.editingSupport()
      ?.getActions()
      .filter((action) => action.scopes.includes(scope) && action.id === actionId);
    return matchingActions && matchingActions.length > 0 ? matchingActions[0] : null;
  }

  protected async executeAction(action: Action<T>, row?: T) {
    try {
      if (row) {
        this.isRunningAction.set(true);
      }
      await action.on?.(row);
      this.dataSource().refreshFromRoute(this.route);
    } catch (error) {
      console.error(error);
    } finally {
      this.isRunningAction.set(false);
    }
  }

  protected sortSettingsChanged(event: Sort) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        sortBy: event.active,
        sortDirection: event.direction ?? 'asc',
      },
      queryParamsHandling: 'merge',
    });
  }

  private mergeQueryParams(
    oldQueryParams: ParamMap,
    filterValues: FilterValues,
  ): Record<string, string | null> {
    const existingQueryParams = Object.fromEntries(
      oldQueryParams.keys.map((key) => [key, oldQueryParams.get(key)]),
    ) as Record<string, string | null>;

    const filterQueryParams = Object.fromEntries(
      Object.entries(filterValues).map(([key, value]) => [key, value || null]),
    ) as Record<string, string | null>;

    return {
      // Preserve existing query params (e.g. requirementId) and overwrite only filter params.
      ...existingQueryParams,
      // Empty filter values are set to null so they are removed from the URL.
      ...filterQueryParams,
    };
  }

  filtersChanged(filterValues: FilterValues) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.mergeQueryParams(this.route.snapshot.queryParamMap, filterValues),
      queryParamsHandling: 'replace',
    });
  }

  protected handleColumnsChanged(columnIds: string[]): void {
    this.selectedColumnIds.set(columnIds);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        ...this.mergeQueryParams(
          this.route.snapshot.queryParamMap,
          this.getNonColumnFilterValues(),
        ),
        columns: columnIds.length > 0 ? columnIds.join(',') : null,
      },
      queryParamsHandling: 'replace',
    });
  }

  private getNonColumnFilterValues(): FilterValues {
    const filters = this.tableFilters().filter((filter) => filter.type !== 'columns');
    return Object.fromEntries(
      filters.map((filter) => [filter.id, this.route.snapshot.queryParamMap.get(filter.id) ?? '']),
    ) as FilterValues;
  }
}
